import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { errMsg } from "../lib/api";
import { calcTotals, todayIST, ddmmyyyy, PRODUCT_LINES } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useClient } from "../context/ClientContext";
import { VendorPicker } from "../components/po/VendorPicker";
import { ItemCard } from "../components/po/ItemCard";
import { TotalsBar } from "../components/po/TotalsBar";

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Math.random()).slice(2));
const blankItem = () => ({ key: uid(), description: "", code: "", colour: "", qty: "", uom: "Mtr", rate: "", gst: 5, photos: [] });

const Section = ({ title, children, testid }) => (
  <section className="panel rise space-y-4 p-4" data-testid={testid}>
    <h2 className="mono-label text-muted-foreground">{title}</h2>
    {children}
  </section>
);

function validate(form, quick) {
  if (!form.vendor_id) return "Please select a vendor";
  const filled = form.items.filter((it) => it.description.trim() || Number(it.qty) > 0);
  if (!filled.length) return "Add at least one item with a description and quantity above 0";
  for (const it of filled) {
    if (!it.description.trim()) return "Every item needs a description";
    if (!(Number(it.qty) > 0)) return `Quantity must be above 0 for "${it.description}"`;
  }
  if (quick && !form.raised_by.trim()) return "Please enter your name in 'Raised by'";
  return null;
}

export default function POForm() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { client: api, quick, base } = useClient();
  const userName = user?.name;
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(0);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [known, setKnown] = useState([]);
  const vendorId = form?.vendor_id;
  const line = form?.product_line;
  useEffect(() => {
    if (!vendorId) { setKnown([]); return; }
    api.get("/items", { params: { vendor_id: vendorId, product_line: line } }).then((r) => setKnown(r.data)).catch(() => setKnown([]));
  }, [vendorId, line, api]);

  useEffect(() => {
    let cancelled = false;
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
    if (id) {
      api.get(`/pos/${id}`).then(({ data: po }) => {
        if (cancelled) return;
        if (po.status !== "Draft") {
          toast.error("Only Draft POs can be edited", { id: "edit-guard" });
          navigate("/pos");
          return;
        }
        setForm({ ...po, vendor_id: po.vendor.vendor_id, expected_delivery: po.expected_delivery || "", payment_terms: po.payment_terms || "",
          items: po.items.map((it) => ({ ...it, key: it.id })) });
      }).catch((e) => toast.error(errMsg(e)));
    } else {
      api.get("/settings").then(({ data: s }) => setForm({
        vendor_id: "", product_line: PRODUCT_LINES[0], po_date: todayIST(), expected_delivery: "", payment_terms: "",
        deliver_to: s.deliver_to || "", items: [blankItem()], remarks: "",
        raised_by: localStorage.getItem("mm-raised-by") || userName || "",
      })).catch((e) => toast.error(errMsg(e), { id: "po-form-load" }));
    }
    return () => { cancelled = true; };
  }, [id, navigate, userName, api]);

  const totals = useMemo(() => calcTotals(form?.items || []), [form]);
  if (!form) return <p className="text-muted-foreground" data-testid="po-form-loading">Loading…</p>;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const setItem = (key, patch) => setForm((f) => ({ ...f, items: f.items.map((it) => (it.key === key ? { ...it, ...patch } : it)) }));
  const removeItem = (key) => setForm((f) => ({ ...f, items: f.items.length > 1 ? f.items.filter((it) => it.key !== key) : [blankItem()] }));
  const addColour = (key) => setForm((f) => {
    const i = f.items.findIndex((it) => it.key === key);
    const src = f.items[i];
    const copy = { ...blankItem(), description: src.description, code: src.code, uom: src.uom, rate: src.rate, gst: src.gst };
    return { ...f, items: [...f.items.slice(0, i + 1), copy, ...f.items.slice(i + 1)] };
  });

  const save = async () => {
    setTried(true);
    const err = validate(form, quick);
    if (err) return toast.error(err);
    if (uploading) return toast.error("Please wait for photos to finish uploading");
    setSaving(true);
    const payload = {
      vendor_id: form.vendor_id, product_line: form.product_line, po_date: form.po_date,
      expected_delivery: form.expected_delivery || null, payment_terms: form.payment_terms || "", deliver_to: form.deliver_to, remarks: form.remarks,
      raised_by: form.raised_by,
      items: form.items.filter((it) => it.description.trim() || Number(it.qty) > 0).map((it) => ({
        id: it.id, description: it.description, code: it.code, colour: it.colour || "", qty: Number(it.qty) || 0, uom: it.uom,
        rate: Number(it.rate) || 0, gst: Number(it.gst), photos: it.photos,
      })),
    };
    try {
      const { data } = id ? await api.put(`/pos/${id}`, payload) : await api.post("/pos", payload);
      if (form.raised_by) localStorage.setItem("mm-raised-by", form.raised_by);
      toast.success(`${data.po_no} saved`);
      navigate(`${base}/pos/${data.id}/saved`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="po-form">
      <div>
        <p className="mono-label text-brand">{id ? `Editing ${form.po_no}` : "Draft"}</p>
        <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{id ? "Edit purchase order" : "New purchase order"}</h1>
      </div>

      <Section title="Vendor" testid="po-vendor-section">
        <VendorPicker vendors={vendors} value={form.vendor_id} onChange={set("vendor_id")} invalid={tried && !form.vendor_id} />
      </Section>

      <Section title="Order details" testid="po-order-section">
        <div>
          <span className="label">Product line</span>
          <div className="seg">
            {PRODUCT_LINES.map((p) => (
              <button type="button" key={p} data-active={form.product_line === p} onClick={() => set("product_line")(p)}
                data-testid={`product-line-${p.split(" ")[0].toLowerCase()}`}>{p}</button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 min-[400px]:grid-cols-2">
          <div className="min-w-0">
            <label className="label" htmlFor="po-date">PO date</label>
            <input id="po-date" type="date" className="field" value={form.po_date} onChange={set("po_date")} required data-testid="po-date-input" />
            <p className="mono mt-1 text-xs text-muted-foreground" data-testid="po-date-display">{ddmmyyyy(form.po_date)}</p>
          </div>
          <div className="min-w-0">
            <label className="label" htmlFor="po-delivery">Expected delivery</label>
            <input id="po-delivery" type="date" className="field" value={form.expected_delivery} min={form.po_date}
              onChange={set("expected_delivery")} data-testid="po-delivery-input" />
            <p className="mono mt-1 text-xs text-muted-foreground" data-testid="po-delivery-display">{ddmmyyyy(form.expected_delivery) || "DD/MM/YYYY"}</p>
          </div>
        </div>
        <div>
          <label className="label" htmlFor="po-payment-terms">Payment terms</label>
          <input id="po-payment-terms" className="field" value={form.payment_terms} onChange={set("payment_terms")}
            placeholder={`${vendors.find((v) => v.id === form.vendor_id)?.payment_days ?? 0} days from invoice date (vendor default)`} data-testid="po-payment-terms-input" />
        </div>
        <div>
          <label className="label" htmlFor="po-deliver-to">Deliver to</label>
          <textarea id="po-deliver-to" className="field" rows={2} value={form.deliver_to} onChange={set("deliver_to")} data-testid="po-deliver-to-input" />
        </div>
      </Section>

      <div className="space-y-3" data-testid="po-items-section">
        <h2 className="mono-label px-1 text-muted-foreground">Items ({form.items.length})</h2>
        {form.items.map((it, i) => (
          <ItemCard key={it.key} index={i} item={it} tried={tried} onChange={(p) => setItem(it.key, p)} known={known} onAddColour={() => addColour(it.key)}
            onRemove={() => removeItem(it.key)} onUploading={(d) => setUploading((u) => u + d)} />
        ))}
        <button type="button" className="btn btn-outline w-full border-dashed" onClick={() => setForm({ ...form, items: [...form.items, blankItem()] })}
          data-testid="add-item-btn"><Plus size={18} /> Add item</button>
      </div>

      <Section title="Remarks" testid="po-remarks-section">
        <textarea className="field" rows={2} value={form.remarks} onChange={set("remarks")} placeholder="Optional notes for the vendor" data-testid="po-remarks-input" />
        <div>
          <label className="label" htmlFor="raised-by">Raised by</label>
          <input id="raised-by" className="field" value={form.raised_by} onChange={set("raised_by")} data-testid="po-raised-by-input" />
        </div>
      </Section>

      <TotalsBar totals={totals} onSave={save} saving={saving} uploading={uploading > 0} label={id ? "Update PO" : "Save PO"} />
    </div>
  );
}
